import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Award, Shield, TrendingUp, Users, CheckCircle } from "lucide-react";

const LOGO = "/sneha-logo.jpg";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
};

const About = () => {
  const values = [
    { icon: Shield, title: "Zero Conflicts", desc: "We are fee-based advisors, not commission-driven brokers. Our interest is your returns." },
    { icon: TrendingUp, title: "Data-Driven", desc: "Every recommendation is backed by infrastructure analysis, historical data, and market intelligence." },
    { icon: Award, title: "Verified Assets", desc: "100% verified properties from RERA-registered developers. Zero hidden charges, ever." },
    { icon: Users, title: "Investor First", desc: "We've served 500+ investors and managed ₹1000+ Cr in advisory mandates. Your trust drives us." },
  ];

  const team = [
    { name: "Sneha Patil", role: "Founder & Chief Investment Officer", exp: "15+ years in Navi Mumbai real estate" },
    { name: "Rahul Desai", role: "Head of Infrastructure Intelligence", exp: "Ex-CIDCO urban planner, 12 years" },
    { name: "Priyanka Shah", role: "NRI Investment Specialist", exp: "Served 200+ NRI clients across 15 countries" },
    { name: "Vikram Nair", role: "Commercial Real Estate Head", exp: "10+ years in commercial & retail assets" },
  ];

  return (
    <div className="bg-[#080808] min-h-screen pt-24">
      {/* Header */}
      <div className="py-20 border-b border-white/5 relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #0a0900 0%, #080808 60%, #0a0a08 100%)" }}>
        <div className="absolute inset-0 opacity-10"
          style={{ background: "radial-gradient(ellipse 60% 60% at 50% 50%, rgba(212,175,55,0.15), transparent)" }} />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10 text-center">
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
            <img src={LOGO} alt="Sneha Reality" className="h-24 w-auto object-contain mx-auto mb-6"
              style={{ filter: "drop-shadow(0 0 20px rgba(212,175,55,0.3))" }} />
          </motion.div>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="section-label mb-4">About Sneha Reality</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="font-display text-4xl md:text-5xl font-light text-[#FAF8F3] max-w-2xl mx-auto">
            We Don't Sell Property.<br />
            <span className="text-gold-gradient italic">We Engineer Wealth.</span>
          </motion.h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 space-y-20">
        {/* Story */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div {...fadeUp}>
            <p className="section-label mb-4">Our Story</p>
            <h2 className="font-display text-3xl text-[#FAF8F3] font-light mb-6">
              Built on One Belief:<br />
              <span className="text-gold-gradient italic">Infrastructure Creates Wealth</span>
            </h2>
            <div className="space-y-4 text-[#A0A0A0] text-sm leading-relaxed">
              <p>Sneha Reality was founded with a singular conviction — that infrastructure development, not just market timing, is the most reliable predictor of real estate wealth.</p>
              <p>We identified Navi Mumbai's transformation potential years before it became mainstream news. When others saw just infrastructure, we saw a 10-year wealth creation opportunity for our clients.</p>
              <p>Today, we are Navi Mumbai's most trusted investment advisory firm — not brokers, not developers, but pure wealth architects with skin in the game.</p>
            </div>
          </motion.div>

          <motion.div {...fadeUp} transition={{ delay: 0.2 }}
            className="grid grid-cols-2 gap-4">
            {[
              { value: "₹1000+ Cr", label: "Assets Under Advisory" },
              { value: "500+", label: "Investors Served" },
              { value: "15+", label: "Years of Expertise" },
              { value: "0%", label: "Hidden Charges" },
            ].map((m) => (
              <div key={m.label} className="glass-gold p-6 text-center gold-card">
                <p className="font-display text-3xl text-[#D4AF37] font-light mb-2">{m.value}</p>
                <p className="text-[#666] text-xs tracking-wider">{m.label}</p>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Values */}
        <div>
          <motion.p {...fadeUp} className="section-label mb-3 text-center">Our Values</motion.p>
          <motion.h2 {...fadeUp} transition={{ delay: 0.1 }}
            className="font-display text-3xl text-[#FAF8F3] font-light mb-10 text-center">
            The <span className="text-gold-gradient italic">Sneha Reality</span> Difference
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => (
              <motion.div key={v.title}
                {...fadeUp} transition={{ delay: i * 0.1 }}
                className="gold-card glass-gold p-6 text-center"
              >
                <div className="w-12 h-12 border border-[#D4AF37]/20 flex items-center justify-center mx-auto mb-4">
                  <v.icon size={20} className="text-[#D4AF37]" />
                </div>
                <h3 className="font-heading text-[#FAF8F3] font-light text-sm mb-3">{v.title}</h3>
                <p className="text-[#666] text-xs leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Team */}
        <div>
          <motion.p {...fadeUp} className="section-label mb-3">Our Team</motion.p>
          <motion.h2 {...fadeUp} transition={{ delay: 0.1 }}
            className="font-display text-3xl text-[#FAF8F3] font-light mb-10">
            Your Wealth<br />
            <span className="text-gold-gradient italic">Advisory Team</span>
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {team.map((m, i) => (
              <motion.div key={m.name}
                {...fadeUp} transition={{ delay: i * 0.1 }}
                className="glass-gold p-6 gold-card"
              >
                <div className="w-12 h-12 border border-[#D4AF37]/20 flex items-center justify-center mb-4 font-display text-lg text-[#D4AF37]">
                  {m.name.split(" ").map((n) => n[0]).join("")}
                </div>
                <h3 className="font-heading text-[#FAF8F3] font-light text-sm mb-1">{m.name}</h3>
                <p className="text-[10px] text-[#D4AF37] tracking-wider mb-3">{m.role}</p>
                <p className="text-xs text-[#666]">{m.exp}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <motion.div {...fadeUp} className="text-center glass-gold p-12">
          <img src={LOGO} alt="Sneha Reality" className="h-16 w-auto object-contain mx-auto mb-6" />
          <h3 className="font-display text-3xl text-[#FAF8F3] mb-3">Ready to Engineer Your Wealth?</h3>
          <p className="text-[#A0A0A0] text-sm mb-8 max-w-md mx-auto">
            Join 500+ investors who trust Sneha Reality to navigate Navi Mumbai's most promising real estate opportunities.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/consultation" className="btn-gold-solid btn-gold text-xs py-3 px-8">
              Book Advisory Session <ArrowRight size={12} className="inline ml-2" />
            </Link>
            <Link to="/properties" className="btn-gold text-xs py-3 px-8">
              Explore Properties
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default About;
